import { isFunction } from 'lodash';
import React from 'react';
import styled from 'styled-components';

import { variables } from '@splunk/themes';

import { LocaleInfo, formatTimestamp } from './utilities/formatTimestamp';
import { formatDurationMicros } from './utilities/formatDuration';
import { BasicEventAnnotationTooltip } from './AnnotationTooltip';
import { EventAnnotation, EventAnnotationConfig } from './EventAnnotations';
import { Alert, DefaultAnnotationLabels, CriticalSeverity } from './constants';
import { CursorState } from './cursorPlugin';
import { PlotUtilities, SeriesInfo } from './renderers/types';
import { useVizTheme, VizTheme } from './theme';
import { AdditionalTooltipValues, DataSet, GenericDataSet, ScaleConfig, SeriesConfig } from './types';
import {
    calculateTooltipLinePosition,
    exists,
    formatValueWithSeriesConfig,
    getMergedSeriesConfigForField,
    getXAxisResolution,
    SECOND_TO_MICROSECOND,
} from './utilities/utils';
import { useScaleFactor } from './utilities/useScaleFactor';

export type TooltipProps = {
    plotUtilities: PlotUtilities;
    cursorState: CursorState;
    data: GenericDataSet;
    seriesConfig: SeriesConfig;
    scaleConfig: ScaleConfig;
    // eslint-disable-next-line react/no-unused-prop-types
    direction: 'right' | 'left';
    // eslint-disable-next-line react/no-unused-prop-types
    chartTitle?: string;
    // eslint-disable-next-line react/no-unused-prop-types
    xAxisResolution?: number;
    userLocale?: LocaleInfo;
    // eslint-disable-next-line react/no-unused-prop-types
    hideTooltipSeriesLabel?: boolean;
    isTimeSeries?: boolean;
    rawXaxisValues?: (string | number | null)[];
    // eslint-disable-next-line react/no-unused-prop-types
    additionalTooltipValues?: AdditionalTooltipValues;
};

export type Tooltip = React.ComponentType<TooltipProps>;

type TooltipLabelFn = (props: TooltipProps) => React.ReactNode;

type BasicValueTooltipProps = TooltipProps & {
    tooltipLabelComponent?: React.ReactNode | TooltipLabelFn;
    precomponent?: React.ReactNode | TooltipLabelFn;
};

const StyledPointValue = styled.div`
    display: flex;
    flex-direction: row;
`;

const StyledSeriesIcon = styled.div<{ series: SeriesInfo }>`
    margin-top: 7px;
    margin-right: 8px;
    width: 7px;
    height: 7px;
    border-radius: 5px;
    background-color: ${(props): string =>
        props.series.fill?.toString() || props.series.stroke?.toString() || 'transparent'};
`;

const StyledResolution = styled.div`
    color: #999999;
    margin-left: 8px;
`;

const StyledSeriesLabel = styled.div`
    margin-top: 7px;
    color: white;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const StyledWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: ${variables.spacingXSmall};
    align-self: stretch;
`;

const StyledFormattedDate = styled.div`
    color: ${variables.contentColorDefault};
    font-size: 10px;
    font-style: normal;
    line-height: ${variables.spacingLarge};
`;

const StyledFormattedValue = styled.div`
    color: ${variables.contentColorActive};
    font-size: 10px;
    font-style: normal;
    font-weight: ${variables.fontWeightBold};
    line-height: ${variables.spacingLarge};
    flex: 1 0 0;
`;

// Common tooltip container styles
const getTooltipContainerStyles = (theme: VizTheme) => ({
    borderRadius: 5,
    backgroundColor: theme.tooltip.backgroundColor,
    color: theme.tooltip.color,
    margin: '0 5px',
    padding: '5px 10px',
    fontSize: '14px',
    textAlign: 'left' as const,
});

// Multivalue tooltip container with min/max width constraints
const StyledMultiValueTooltipContainer = styled.div<{ theme: VizTheme }>`
    border-radius: 5px;
    background-color: ${({ theme }) => theme.tooltip.backgroundColor};
    color: ${({ theme }) => theme.tooltip.color};
    margin: 0 5px;
    padding: 5px 10px;
    font-size: 14px;
    text-align: left;
    min-width: 100px;
    overflow: hidden;
    box-sizing: border-box;
`;

// Series row: icon + label on left, value pinned to right
const StyledMultiValueSeriesRow = styled.div<{ isFocused: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    overflow: hidden;
    font-weight: ${({ isFocused }) => (isFocused ? 'bolder' : 'normal')};
`;

// Label portion: truncates with ellipsis when too long
const StyledMultiValueSeriesLabel = styled.div`
    display: flex;
    flex-direction: row;
    align-items: baseline;
    min-width: 0;
    flex: 1;
    overflow: hidden;
`;

const StyledMultiValueSeriesName = styled.span`
    display: block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// Value stays on the right, never truncated
const StyledMultiValueSeriesValue = styled.div`
    flex-shrink: 0;
    margin-left: 8px;
    text-align: right;
`;

// Inline colored dot for multivalue rows (same appearance as StyledSeriesIcon but flex-shrink: 0)
const StyledMultiValueSeriesIcon = styled.div<{ fill: string }>`
    margin-top: 7px;
    margin-right: 8px;
    flex-shrink: 0;
    width: 7px;
    height: 7px;
    border-radius: 5px;
    background-color: ${({ fill }) => fill};
`;

// Common formatted data styles
const getFormattedDataStyles = () => ({
    color: '#ccc',
    fontSize: '12px',
    marginBottom: 1,
});

// Helper function to evaluate tooltip components
const evaluateTooltipComponent = <T extends React.ReactNode | TooltipLabelFn>(
    component: T,
    props: TooltipProps
): React.ReactNode => {
    if (isFunction(component)) {
        return component(props);
    }
    return component;
};

// Helper function to get formatted date for tooltips
const getTooltipFormattedData = (
    cursorState: CursorState,
    data: GenericDataSet,
    userLocale: LocaleInfo | undefined,
    isTimeSeries: boolean | undefined,
    rawXaxisValues: (string | number | null)[] | undefined,
    cursorIndex: number | undefined
): React.ReactNode => {
    if (isTimeSeries) {
        return getFormattedDate(cursorState, data as DataSet, userLocale);
    }
    return rawXaxisValues?.[cursorIndex] ?? '';
};

// Common formatted data display component
const FormattedDataDisplay: React.FC<{ formattedData: React.ReactNode }> = ({ formattedData }) => {
    if (!formattedData) return null;
    return <div style={getFormattedDataStyles()}>{formattedData}</div>;
};

// Styled component for additional tooltip fields container
const StyledAdditionalFieldsContainer = styled.div`
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

// Styled component for individual additional field row
const StyledAdditionalFieldRow = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 12px;
    margin-bottom: 2px;
`;

const StyledAdditionalFieldLabel = styled.span`
    color: #999;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const StyledAdditionalFieldValue = styled.span`
    color: #fff;
    flex-shrink: 0;
    text-align: right;
    white-space: nowrap;
`;

// Helper component to render additional tooltip fields
// Simple implementation - parent tooltip components control when this renders
const AdditionalTooltipFieldsDisplay: React.FC<{
    additionalTooltipValues?: AdditionalTooltipValues;
    cursorIndex?: number;
}> = ({ additionalTooltipValues, cursorIndex }) => {
    if (!additionalTooltipValues?.length || cursorIndex === undefined) {
        return null;
    }

    const valuesAtIndex = additionalTooltipValues[cursorIndex];
    if (!valuesAtIndex) {
        return null;
    }

    const entries = Object.entries(valuesAtIndex);
    if (entries.length === 0) {
        return null;
    }

    return (
        <StyledAdditionalFieldsContainer data-test="additional-tooltip-fields">
            {entries.map(([fieldName, value]) => {
                // Safely convert value to string - only string and number types are expected
                let displayValue = '-';
                if (value !== null && value !== undefined) {
                    if (typeof value === 'string' || typeof value === 'number') {
                        displayValue = String(value);
                    }
                }
                return (
                    <StyledAdditionalFieldRow key={fieldName}>
                        <StyledAdditionalFieldLabel>{fieldName}:</StyledAdditionalFieldLabel>
                        <StyledAdditionalFieldValue data-test={`additional-field-${fieldName}`}>
                            {displayValue}
                        </StyledAdditionalFieldValue>
                    </StyledAdditionalFieldRow>
                );
            })}
        </StyledAdditionalFieldsContainer>
    );
};

const getFormattedDate = (cursorState: CursorState, data: DataSet, locale: LocaleInfo): React.ReactNode => {
    let formattedDate: React.ReactNode = null;
    if (cursorState.cursor) {
        const time = data.columns[0][cursorState.cursor?.index] as number;
        if (time) {
            formattedDate = formatTimestamp(time * 1000, 'dateVerboseTimePrecise', locale);
            if ('utcOffset' in locale) {
                formattedDate += locale.utcOffset > 0 ? `+${locale.utcOffset}` : locale.utcOffset.toString();
            }
        }
    }
    return formattedDate;
};

const getFormattedValue = (
    plotUtilities: PlotUtilities,
    cursorState: CursorState,
    data: GenericDataSet,
    seriesConfig: SeriesConfig,
    scaleConfig: ScaleConfig,
    seriesIndex: number
): React.ReactNode => {
    const selectedSeries = plotUtilities.allSeries[seriesIndex];
    let formattedValue: React.ReactNode = '-';

    if (cursorState.cursor) {
        const v = data.columns[seriesIndex][cursorState.cursor?.index];
        const config = getMergedSeriesConfigForField(selectedSeries.field, seriesConfig, scaleConfig);
        if (v !== null) {
            formattedValue = formatValueWithSeriesConfig(v as number, config, data);
        }
    }
    return formattedValue;
};

const hasCursorState = (
    cursorState: CursorState | undefined,
    lowerBound: number,
    upperBound: number
): boolean =>
    cursorState != null &&
    cursorState.state !== 'off' &&
    cursorState.cursor != null &&
    cursorState.cursor.left >= lowerBound &&
    cursorState.cursor.left <= upperBound &&
    cursorState.seriesIndex != null;

export const BasicMultiValueTooltip: React.FC<BasicValueTooltipProps> = props => {
    const {
        plotUtilities,
        seriesConfig,
        scaleConfig,
        data,
        cursorState,
        userLocale,
        isTimeSeries,
        rawXaxisValues,
        additionalTooltipValues,
    } = props;
    const theme = useVizTheme();
    const cursorIndex = cursorState.cursor?.index;
    const formattedData = getTooltipFormattedData(
        cursorState,
        data,
        userLocale,
        isTimeSeries,
        rawXaxisValues,
        cursorIndex
    );

    // Evaluate tooltip components
    const tooltipLabelComponent = evaluateTooltipComponent(props.tooltipLabelComponent, props);
    const precomponent = evaluateTooltipComponent(props.precomponent, props);

    return (
        <StyledMultiValueTooltipContainer theme={theme}>
            {tooltipLabelComponent && <div>{tooltipLabelComponent}</div>}
            <FormattedDataDisplay formattedData={formattedData} />
            {precomponent && <div>{precomponent}</div>}
            {plotUtilities.allSeries.slice(1).map((s, i) => {
                const formattedValue = getFormattedValue(
                    plotUtilities,
                    cursorState,
                    data,
                    seriesConfig,
                    scaleConfig,
                    i + 1
                );

                return (
                    <StyledMultiValueSeriesRow key={s.label} isFocused={i + 1 === cursorState.seriesIndex}>
                        <StyledMultiValueSeriesLabel>
                            <StyledMultiValueSeriesIcon
                                fill={s.fill?.toString() || s.stroke?.toString() || 'transparent'}
                            />
                            <StyledMultiValueSeriesName>{s.label}</StyledMultiValueSeriesName>
                        </StyledMultiValueSeriesLabel>
                        <StyledMultiValueSeriesValue>{formattedValue}</StyledMultiValueSeriesValue>
                    </StyledMultiValueSeriesRow>
                );
            })}
            <AdditionalTooltipFieldsDisplay
                additionalTooltipValues={additionalTooltipValues}
                cursorIndex={cursorIndex}
            />
        </StyledMultiValueTooltipContainer>
    );
};

type BasicValueTooltipFactoryProps = {
    precomponent: React.ReactNode | TooltipLabelFn;
    tooltipLabelComponent?: React.ReactNode | TooltipLabelFn;
};

export const BasicMultiValueTooltipFactory: (
    props: BasicValueTooltipFactoryProps
) => React.FC<BasicValueTooltipProps> = ({ precomponent: label, tooltipLabelComponent }) => {
    const component: React.FC<TooltipProps> = componentProps => (
        <BasicMultiValueTooltip
            {...componentProps}
            precomponent={label}
            tooltipLabelComponent={tooltipLabelComponent}
        />
    );

    return component;
};

export const BasicSingleValueTooltip: React.FC<BasicValueTooltipProps> = props => {
    const {
        plotUtilities,
        seriesConfig,
        scaleConfig,
        data,
        cursorState,
        chartTitle,
        xAxisResolution,
        userLocale,
        hideTooltipSeriesLabel,
        isTimeSeries,
        rawXaxisValues,
        additionalTooltipValues,
    } = props;

    const resolutionInSeconds = xAxisResolution || getXAxisResolution(props.data);
    const resolutionText = formatDurationMicros(resolutionInSeconds * SECOND_TO_MICROSECOND);
    const theme = useVizTheme();

    if (cursorState.state === 'off' || cursorState.seriesIndex === null) {
        return null;
    }

    const selectedSeries = plotUtilities.allSeries[cursorState.seriesIndex];
    const showTooltipSeriesLabel = chartTitle !== selectedSeries.label && !hideTooltipSeriesLabel;
    const cursorIndex = cursorState.cursor?.index;
    const formattedData = getTooltipFormattedData(
        cursorState,
        data,
        userLocale,
        isTimeSeries,
        rawXaxisValues,
        cursorIndex
    );
    const formattedValue = getFormattedValue(
        plotUtilities,
        cursorState,
        data,
        seriesConfig,
        scaleConfig,
        cursorState.seriesIndex
    );

    // Evaluate tooltip components
    const tooltipLabelComponent = evaluateTooltipComponent(props.tooltipLabelComponent, props);
    const precomponent = evaluateTooltipComponent(props.precomponent, props);

    return (
        <div style={getTooltipContainerStyles(theme)}>
            {tooltipLabelComponent && <div>{tooltipLabelComponent}</div>}
            <FormattedDataDisplay formattedData={formattedData} />
            {precomponent && <div>{precomponent}</div>}
            <div key={selectedSeries.label}>
                <StyledSeriesLabel>
                    {showTooltipSeriesLabel && <div>{chartTitle}</div>}
                    <div
                        data-test="timeseries-tooltip-label"
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                        {selectedSeries.label}
                    </div>
                </StyledSeriesLabel>
                <StyledPointValue>
                    <StyledSeriesIcon series={selectedSeries} />
                    <div data-test="timeseries-tooltip-value">{formattedValue}</div>
                    {isTimeSeries && (
                        <StyledResolution data-test="timeseries-tooltip-resolution">
                            resolution {resolutionText}
                        </StyledResolution>
                    )}
                </StyledPointValue>
            </div>
            <AdditionalTooltipFieldsDisplay
                additionalTooltipValues={additionalTooltipValues}
                cursorIndex={cursorIndex}
            />
        </div>
    );
};

type TooltipOutsideChartProps = {
    plotUtilities?: PlotUtilities;
    cursorState?: CursorState;
    data: GenericDataSet;
    seriesConfig?: SeriesConfig;
    scaleConfig?: ScaleConfig;
    userLocale?: LocaleInfo;
    eventAnnotationCursorState?: EventAnnotation;
    isTimeSeries?: boolean;
    rawXaxisValues?: (string | number | null)[];
};

export const TooltipOutsideChart: React.FC<TooltipOutsideChartProps> = ({
    plotUtilities,
    seriesConfig = {},
    scaleConfig = {},
    data,
    cursorState,
    userLocale,
    eventAnnotationCursorState,
    isTimeSeries = true,
    rawXaxisValues,
}) => {
    if (
        plotUtilities === undefined ||
        data === undefined ||
        (!hasCursorState(cursorState, 0, plotUtilities.dimensions.over.width) && !eventAnnotationCursorState)
    ) {
        return null;
    }

    let formattedData: React.ReactNode = null;
    let formattedValue: React.ReactNode = '-';

    // Render annotation tooltip if cursor is hovered over annotation
    if (eventAnnotationCursorState) {
        formattedData = `${formatTimestamp(
            eventAnnotationCursorState.time * 1000,
            'dateVerboseTimePrecise',
            userLocale
        )}:`;
        formattedValue =
            eventAnnotationCursorState.type === Alert
                ? DefaultAnnotationLabels[eventAnnotationCursorState.severity || CriticalSeverity]
                : eventAnnotationCursorState.key;
    } else if (cursorState?.seriesIndex) {
        // The above condition is not needed since tooltip other than annotation tooltip would be tooltip for points inside chart, but has been added to satisfy eslint checks.
        const tooltipData = getTooltipFormattedData(
            cursorState,
            data,
            userLocale,
            isTimeSeries,
            rawXaxisValues,
            cursorState.cursor?.index
        );
        formattedData = isTimeSeries ? `${tooltipData}:` : tooltipData;

        formattedValue = getFormattedValue(
            plotUtilities,
            cursorState,
            data,
            seriesConfig,
            scaleConfig,
            cursorState.seriesIndex
        );
    }

    return (
        <StyledWrapper>
            {formattedData && (
                <StyledFormattedDate data-test="tooltip-data">{formattedData}</StyledFormattedDate>
            )}
            <StyledFormattedValue data-test="tooltip-value">{formattedValue}</StyledFormattedValue>
        </StyledWrapper>
    );
};

export const BasicSingleValueTooltipFactory: (
    props: BasicValueTooltipFactoryProps
) => React.FC<BasicValueTooltipProps> = ({ precomponent: label, tooltipLabelComponent }) => {
    const component: React.FC<TooltipProps> = componentProps => (
        <BasicSingleValueTooltip
            {...componentProps}
            precomponent={label}
            tooltipLabelComponent={tooltipLabelComponent}
        />
    );

    return component;
};

export type EventAnnotationTooltipProps = {
    eventAnnotationCursorState: EventAnnotation;
    eventAnnotationConfig?: EventAnnotationConfig;
    direction: 'right' | 'left';
    theme: VizTheme;
    userLocale?: LocaleInfo;
};

export type EventAnnotationTooltip = React.ComponentType<EventAnnotationTooltipProps>;

type InternalTooltipContainerProps = {
    direction: 'left' | 'right';
    left: number;
    plotWidth: number;
    active: boolean;
    children: React.ReactNode;
    scaleFactor: number;
    isEventAnnotation?: boolean;
};

const InternalTooltipContainer: React.FC<InternalTooltipContainerProps> = props => {
    const { direction, left, plotWidth, active, scaleFactor, isEventAnnotation = false } = props;
    const theme = useVizTheme();
    const scaledPlotWidth = plotWidth / scaleFactor;
    const tooltipLineLeft = isEventAnnotation ? calculateTooltipLinePosition(left, scaleFactor) : left - 0.5;

    // Use ref to update cursor line position directly without React re-render lag
    const lineRef = React.useRef<HTMLDivElement>(null);

    // useLayoutEffect runs synchronously after DOM updates but before paint
    React.useLayoutEffect(() => {
        if (lineRef.current) {
            lineRef.current.style.left = `${tooltipLineLeft}px`;
        }
    }, [tooltipLineLeft]);

    return (
        <>
            <div
                ref={lineRef}
                className="olly-viz-tooltip-line"
                style={{
                    position: 'absolute',
                    backgroundColor: active ? theme.tooltip.lineStroke : theme.tooltip.inactiveLineStroke,
                    width: '1px',
                    top: 0,
                    bottom: 0,
                    left: `${tooltipLineLeft}px`,
                }}
            />
            {active && (
                <div
                    className="olly-viz-tooltip-body"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: direction === 'right' ? `${left}px` : undefined,
                        right: direction === 'left' ? `${scaledPlotWidth - left}px` : undefined,
                        width: 'fit-content',
                        minWidth: '100px',
                        maxWidth:
                            direction === 'right'
                                ? `min(800px, ${scaledPlotWidth - left}px)`
                                : `min(800px, ${left}px)`,
                    }}
                >
                    {props.children}
                </div>
            )}
        </>
    );
};

type DataSeriesTooltipContainerProps = {
    cursorState?: CursorState;
    data?: GenericDataSet;
    plotUtilities?: PlotUtilities;
    seriesConfig?: SeriesConfig;
    scaleConfig?: ScaleConfig;
    tooltip?: React.FunctionComponent<TooltipProps> | React.ComponentClass<TooltipProps>;
    chartTitle?: string;
    xAxisResolution?: number;
    userLocale?: LocaleInfo;
    hideTooltipSeriesLabel?: boolean;
    showTooltipOutsideChart?: boolean;
    isTimeSeries?: boolean;
    rawXaxisValues?: (string | number | null)[];
    additionalTooltipValues?: AdditionalTooltipValues;
};

export const DataSeriesTooltipContainer: React.FC<DataSeriesTooltipContainerProps> = props => {
    const {
        cursorState,
        data,
        plotUtilities,
        seriesConfig = {},
        scaleConfig = {},
        tooltip: tooltipBody = BasicSingleValueTooltip,
        chartTitle,
        xAxisResolution,
        userLocale,
        hideTooltipSeriesLabel,
        showTooltipOutsideChart = false,
        isTimeSeries = true,
        rawXaxisValues = [],
        additionalTooltipValues,
    } = props;

    const scaleFactor = useScaleFactor();
    if (
        plotUtilities === undefined ||
        !cursorState ||
        cursorState.state === 'off' ||
        cursorState.cursor === null ||
        data === undefined
    ) {
        // Don't render tooltip
        return null;
    }

    const { width: plotWidth } = plotUtilities.dimensions.over;
    const { left } = cursorState.cursor;

    if (left < 0 || left > plotWidth / scaleFactor) {
        return null;
    }

    const direction = left < plotWidth / 2 ? 'right' : 'left';

    return (
        <InternalTooltipContainer
            left={left}
            plotWidth={plotWidth}
            direction={direction}
            active={cursorState.active && !showTooltipOutsideChart}
            scaleFactor={scaleFactor}
        >
            {React.createElement(tooltipBody, {
                data,
                plotUtilities,
                cursorState,
                direction,
                seriesConfig,
                scaleConfig,
                chartTitle,
                xAxisResolution,
                userLocale,
                hideTooltipSeriesLabel,
                isTimeSeries,
                rawXaxisValues,
                additionalTooltipValues,
            })}
        </InternalTooltipContainer>
    );
};

type EventAnnotationTooltipContainerProps = {
    plotUtilities?: PlotUtilities;
    eventAnnotationCursorState?: EventAnnotation;
    eventAnnotationConfig?: EventAnnotationConfig;
    eventAnnotationTooltip?: EventAnnotationTooltip;
    showTooltipOutsideChart?: boolean;
    userLocale?: LocaleInfo;
};

export const EventAnnotationTooltipContainer: React.FC<EventAnnotationTooltipContainerProps> = props => {
    const {
        plotUtilities,
        eventAnnotationCursorState,
        eventAnnotationConfig,
        eventAnnotationTooltip = BasicEventAnnotationTooltip,
        showTooltipOutsideChart = false,
        userLocale,
    } = props;
    const theme = useVizTheme();
    const scaleFactor = useScaleFactor();

    if (!eventAnnotationCursorState || !plotUtilities) {
        return null;
    }

    const { width: plotWidth } = plotUtilities.dimensions.over;
    const left = plotUtilities.valToPos(eventAnnotationCursorState.time, 'x');
    const direction = left < plotWidth / 2 ? 'right' : 'left';

    return (
        <InternalTooltipContainer
            left={left}
            plotWidth={plotWidth}
            direction={direction}
            active={!showTooltipOutsideChart}
            scaleFactor={scaleFactor}
            isEventAnnotation
        >
            {React.createElement(eventAnnotationTooltip, {
                eventAnnotationCursorState,
                eventAnnotationConfig,
                direction,
                theme,
                userLocale,
            })}
        </InternalTooltipContainer>
    );
};

type TooltipContainerProps = {
    plotUtilities?: PlotUtilities;
    tooltip?: false | Tooltip;
    cursorState: CursorState;
    data: GenericDataSet;
    seriesConfig?: SeriesConfig;
    scaleConfig?: ScaleConfig;
    eventAnnotationCursorState?: EventAnnotation;
    eventAnnotationConfig?: EventAnnotationConfig;
    eventAnnotationTooltip?: EventAnnotationTooltip;
    chartTitle?: string;
    xAxisResolution?: number;
    userLocale?: LocaleInfo;
    hideTooltipSeriesLabel?: boolean;
    showTooltipOutsideChart?: boolean;
    isTimeSeries?: boolean;
    rawXaxisValues?: (string | number | null)[];
    additionalTooltipValues?: AdditionalTooltipValues;
};

export const TooltipContainer: React.FC<TooltipContainerProps> = props => {
    const {
        plotUtilities,
        tooltip,
        cursorState,
        data,
        seriesConfig,
        scaleConfig,
        eventAnnotationCursorState,
        eventAnnotationConfig,
        eventAnnotationTooltip,
        chartTitle,
        xAxisResolution,
        userLocale,
        hideTooltipSeriesLabel,
        showTooltipOutsideChart = false,
        isTimeSeries = true,
        rawXaxisValues = [],
        additionalTooltipValues,
    } = props;
    if (tooltip === false) {
        return null;
    }

    if (exists(eventAnnotationCursorState)) {
        return (
            <EventAnnotationTooltipContainer
                plotUtilities={plotUtilities}
                eventAnnotationCursorState={eventAnnotationCursorState}
                eventAnnotationConfig={eventAnnotationConfig}
                eventAnnotationTooltip={eventAnnotationTooltip}
                showTooltipOutsideChart={showTooltipOutsideChart}
                userLocale={userLocale}
            />
        );
    }

    return (
        <DataSeriesTooltipContainer
            cursorState={cursorState}
            data={data}
            plotUtilities={plotUtilities}
            tooltip={tooltip}
            seriesConfig={seriesConfig}
            scaleConfig={scaleConfig}
            chartTitle={chartTitle}
            xAxisResolution={xAxisResolution}
            userLocale={userLocale}
            hideTooltipSeriesLabel={hideTooltipSeriesLabel}
            showTooltipOutsideChart={showTooltipOutsideChart}
            isTimeSeries={isTimeSeries}
            rawXaxisValues={rawXaxisValues}
            additionalTooltipValues={additionalTooltipValues}
        />
    );
};
